import { useState, type FormEvent } from "react";
import { useApiClients, useCreateApiClient, useRevokeApiClient, type CreatedApiKey } from "../api/hooks";
import { DataTable, createColumnHelper } from "../components/DataTable";
import { Button, Chip, ErrorNotice, Field, Input, Modal, Notice, PageHeader } from "../components/ui";
import { IconPlus } from "../components/icons";
import { formatDateTime } from "../lib/format";

/**
 * Claves de API del tenant (CLAUDE.md Fase 4).
 *
 * Dos cosas que la interfaz debe dejar claras porque no tienen vuelta atrás:
 *  - el secreto se muestra **una sola vez**, en el momento de crearla (la API
 *    guarda solo su hash);
 *  - revocar es una baja lógica: la clave deja de funcionar, pero se conserva
 *    la trazabilidad de qué documentos emitió.
 */

const column = createColumnHelper<import("@deca/shared-types").ApiClient>();

const AVAILABLE_SCOPES = [
  { value: "deca:create", label: "Crear documentos" },
  { value: "deca:read", label: "Consultar y descargar documentos" },
  { value: "deca:update", label: "Modificar documentos" },
  { value: "catalog:read", label: "Consultar catálogos" },
  { value: "catalog:write", label: "Gestionar catálogos" },
];

export function ApiKeysPage() {
  const query = useApiClients();
  const create = useCreateApiClient();
  const revoke = useRevokeApiClient();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [scopes, setScopes] = useState<string[]>(["deca:create", "deca:read"]);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [copied, setCopied] = useState(false);

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    create.mutate(
      {
        name: name.trim(),
        scopes,
        webhookUrl: webhookUrl.trim() || null,
      },
      {
        onSuccess: (result) => {
          setCreated(result);
          setName("");
          setWebhookUrl("");
          setOpen(false);
        },
      },
    );
  };

  const columns = [
    column.accessor("name", { header: "Nombre" }),
    column.accessor("scopes", {
      header: "Permisos",
      cell: (info) => (
        <span className="ident text-xs">{info.getValue().join(", ") || "—"}</span>
      ),
    }),
    column.accessor("webhookUrl", {
      header: "Webhook",
      cell: (info) => <span className="text-xs">{info.getValue() ?? "—"}</span>,
    }),
    column.accessor("lastUsedAt", {
      header: "Último uso",
      cell: (info) => <span className="num">{formatDateTime(info.getValue())}</span>,
    }),
    column.accessor("isActive", {
      header: "Estado",
      cell: (info) => (info.getValue() ? <Chip tone="ok">Activa</Chip> : <Chip tone="neutral">Revocada</Chip>),
    }),
    column.display({
      id: "revoke",
      header: "",
      cell: (info) =>
        info.row.original.isActive ? (
          <Button variant="danger" onClick={() => revoke.mutate(info.row.original.id)}>
            Revocar
          </Button>
        ) : null,
    }),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Control"
        title="Claves de API"
        description="Credenciales para que los sistemas de la empresa (ERP, TMS) emitan documentos sin pasar por el panel. Cada clave pertenece a una sola empresa y lleva sus permisos."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setCreated(null);
              setOpen(true);
            }}
          >
            <IconPlus className="h-4 w-4" />
            Nueva clave
          </Button>
        }
      />

      {created ? (
        <div className="mb-4">
          <Notice tone="warn">
            <p className="font-semibold">Copia la clave ahora: no se volverá a mostrar.</p>
            <p className="mt-1">
              El servidor guarda únicamente su huella. Si se pierde, hay que emitir otra.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="ident break-all rounded bg-ink-900 px-2 py-1 text-ink-50">{created.secret}</code>
              <Button
                onClick={() => {
                  void navigator.clipboard.writeText(created.secret).then(() => {
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2000);
                  });
                }}
              >
                {copied ? "Copiada" : "Copiar"}
              </Button>
            </div>
          </Notice>
        </div>
      ) : null}

      {create.isError ? (
        <div className="mb-4">
          <ErrorNotice error={create.error} />
        </div>
      ) : null}
      {revoke.isError ? (
        <div className="mb-4">
          <ErrorNotice error={revoke.error} />
        </div>
      ) : null}

      {query.isError ? (
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={query.data?.items ?? []}
          isLoading={query.isLoading}
          emptyMessage="No hay claves de API. Crea una si algún sistema de la empresa necesita emitir documentos automáticamente."
        />
      )}

      <Modal open={open} title="Nueva clave de API" onClose={() => setOpen(false)}>
        <form onSubmit={onSubmit} className="space-y-3">
          <Field label="Nombre" htmlFor="ak-name" hint="Para reconocerla después: el sistema que la usará.">
            <Input
              id="ak-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="ERP de facturación"
            />
          </Field>

          <fieldset className="rounded-md border border-ink-200 p-3 dark:border-ink-800">
            <legend className="micro-label px-1">Permisos</legend>
            <div className="space-y-1.5">
              {AVAILABLE_SCOPES.map((scope) => (
                <label key={scope.value} className="flex items-center gap-2 text-sm text-ink-700 dark:text-ink-200">
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope.value)}
                    onChange={(event) =>
                      setScopes((previous) =>
                        event.target.checked
                          ? [...previous, scope.value]
                          : previous.filter((item) => item !== scope.value),
                      )
                    }
                  />
                  {scope.label} <span className="ident text-xs text-ink-400">{scope.value}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <Field label="Webhook (opcional)" htmlFor="ak-webhook" hint="Recibirá los avisos de cambio de versión y de descarga desactivada, firmados.">
            <Input
              id="ak-webhook"
              type="url"
              value={webhookUrl}
              onChange={(event) => setWebhookUrl(event.target.value)}
              placeholder="https://ejemplo.es/webhooks/deca"
            />
          </Field>

          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={create.isPending || scopes.length === 0}>
              {create.isPending ? "Creando…" : "Crear clave"}
            </Button>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
